import React, { createContext, useContext, ReactNode, useEffect, useState } from "react";
import { Theme } from "src/contexts/ThemeContext";
import { EUrlParams } from "src/types";

interface UrlParamsContextType {
  theme: Theme;
  storedDataKey: string;
  language: string;
  currencyId: string;
  paymentRouteId: string;
  apiUrl: string;
  walletId: string;
}

const UrlParamsContext = createContext<UrlParamsContextType | undefined>(undefined);

interface UrlParamsProviderProps {
  children: ReactNode;
}

export const UrlParamsProvider: React.FC<UrlParamsProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(Theme.Light);
  const [storedDataKey, setStoredDataKey] = useState<string>("");
  const [language, setLanguage] = useState<string>("en");
  const [currencyId, setCurrencyId] = useState<string>("");
  const [paymentRouteId, setPaymentRouteId] = useState<string>("");
  const [apiUrl, setApiUrl] = useState<string>("");
  const [walletId, setWalletId] = useState<string>("");

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);

    const themeFromUrl = searchParams.get(EUrlParams.Theme);
    const storedDataKeyFromUrl = searchParams.get(EUrlParams.StoredDataKey);
    const languageFromUrl = searchParams.get(EUrlParams.Language);
    const currencyFromUrl = searchParams.get(EUrlParams.CurrencyId);
    const paymentRouteIdFromUrl = searchParams.get(EUrlParams.PaymentRouteId);
    const apiUrlFromUrl = searchParams.get(EUrlParams.ApiUrl);
    const walletIdFromUrl = searchParams.get(EUrlParams.WalletId);

    setStoredDataKey(storedDataKeyFromUrl ?? "");
    setLanguage(languageFromUrl ?? "en");
    setCurrencyId(currencyFromUrl ?? "");
    setPaymentRouteId(paymentRouteIdFromUrl ?? "");
    setApiUrl(apiUrlFromUrl ?? "");
    setWalletId(walletIdFromUrl ?? "");
    setTheme(themeFromUrl && [Theme.Light, Theme.Dark].includes(themeFromUrl as Theme) ? (themeFromUrl as Theme) : Theme.Light);
  }, []);

  const urlParams = {
    theme,
    storedDataKey,
    language,
    currencyId,
    paymentRouteId,
    apiUrl,
    walletId,
  };

  return <UrlParamsContext.Provider value={urlParams}>{children}</UrlParamsContext.Provider>;
};

export const useUrlParamsContext = (): UrlParamsContextType => {
  const context = useContext(UrlParamsContext);

  if (context === undefined) {
    throw new Error("useUrlParamsContext must be used within a UrlParamsProvider");
  }

  return context;
};
